import { FieldValues } from 'react-hook-form';
import {
  useDateFieldController,
  UseDateFieldControllerProps,
} from '../../../hooks/form/useDateFieldController';
import { convertISOStringToDate } from '../../../helpers/dates';
import DatePicker, { DatePickerProps } from '../../library/DatePicker';
import ErrorMessage from '../shared/ErrorMessage';

type DatePickerPropsWithControllerProps<TFieldValues> =
  UseDateFieldControllerProps<TFieldValues> &
    Omit<
      DatePickerProps,
      | 'name'
      | 'value'
      | 'defaultValue'
      | 'disabled'
      | 'onChange'
      | 'onBlur'
      | 'required'
      | 'error'
    > & {
      fullWidth?: boolean;
    };

export interface DateFieldProps<TFieldValues>
  extends DatePickerPropsWithControllerProps<TFieldValues> {}

export default function DateField<TFieldValues extends FieldValues>(
  props: DateFieldProps<TFieldValues>,
) {
  const {
    name,
    onChange,
    onBlur,
    helperText,
    rules,
    shouldUnregister,
    disabled,
    fullWidth,
    ...restDateFieldProps
  } = props;

  const {
    field,
    required,
    onBlurField,
    onChangeField,
    formState: { errors },
    fieldState: { invalid: error },
  } = useDateFieldController({
    name,
    onChange,
    onBlur,
    rules,
    shouldUnregister,
    disabled,
  });

  return (
    <DatePicker
      name={field.name}
      inputRef={field.ref}
      disabled={field.disabled}
      value={convertISOStringToDate(field.value as string | null)}
      onChange={onChangeField}
      error={error}
      required={required}
      fullWidth={fullWidth}
      slotProps={{
        textField: {
          helperText: error ? (
            <ErrorMessage name={name} errors={errors} />
          ) : (
            helperText
          ),
          onBlur: onBlurField,
        },
      }}
      {...restDateFieldProps}
    />
  );
}
